import { Add } from "@mui/icons-material";
import { Box, Button, Checkbox, Chip, Stack, Typography } from "@mui/joy";
import { format } from "date-fns";
import { ApiQuestDetail } from "griptonite-api-client/types";
import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import TextInput, { DateInput, NumberInput } from "../forms/TextInput";
import TimeInput from "../forms/TimeInput";
import { ToggleButtonSelectInput } from "../forms/ToggleButtonSelect";
import { WEEKDAYS, boostToFormValues } from "./boostForms";
import QuestPartCard from "./QuestPartCard";
import { QuestRulesFormValues } from "./rulesForms";

export default function QuestBoosts({ quest }: { quest: ApiQuestDetail }) {
  const { control } = useFormContext<QuestRulesFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "boosts" });

  return (
    <Stack gap={1.5}>
      <Box>
        <Typography level="h4">Boosts</Typography>
        <Typography level="body-sm" color="neutral">
          Multiply the credits climbers earn during set times. Only one boost applies at a time, and
          boosts don&apos;t stack.
        </Typography>
      </Box>
      {fields.map((field, index) => (
        <QuestPartCard
          key={field.id}
          removeLabel={`Remove boost ${index + 1}`}
          onRemove={() => remove(index)}
        >
          <BoostFields quest={quest} index={index} />
        </QuestPartCard>
      ))}
      <Box>
        <Button
          variant="outlined"
          size="sm"
          startDecorator={<Add />}
          onClick={() => append(boostToFormValues())}
        >
          Add boost
        </Button>
      </Box>
    </Stack>
  );
}

const BoostFields = ({ quest, index }: { quest: ApiQuestDetail; index: number }) => {
  const { watch, setValue } = useFormContext<QuestRulesFormValues>();
  const type = watch(`boosts.${index}.type`);
  const uuid = watch(`boosts.${index}.uuid`);
  const hasDates = !!watch(`boosts.${index}.startTime`) || !!watch(`boosts.${index}.endTime`);
  // A weekly boost's dates are optional. Any set dates keep the box ticked, so a date carried over
  // from a one-off boost is never hidden yet still saved.
  const [datesChosen, setDatesChosen] = useState(false);
  const showDates = datesChosen || hasDates;
  const path = (field: string) => `boosts.${index}.${field}`;

  // The pickers only offer days inside the quest's window.
  const minDate = quest.startTime ? format(new Date(quest.startTime), "yyyy-MM-dd") : undefined;
  const maxDate = quest.endTime ? format(new Date(quest.endTime), "yyyy-MM-dd") : undefined;

  return (
    <>
      <Stack direction="row" gap={1.5} alignItems="flex-start">
        <Box sx={{ flex: 2, minWidth: 0 }}>
          <TextInput name={path("name")} label="Name (optional)" placeholder="e.g. Happy hour" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <NumberInput
            name={path("multiplier")}
            label="Multiplier"
            placeholder="e.g. 2"
            decimalScale={2}
          />
        </Box>
      </Stack>
      {uuid && quest.activeBoost === uuid ? (
        <Box>
          <Chip size="sm" color="success" variant="soft">
            Active now
          </Chip>
        </Box>
      ) : null}
      <ToggleButtonSelectInput
        name={path("type")}
        label="Schedule"
        options={[
          { name: "One-off", value: "FIXED" },
          { name: "Repeats weekly", value: "PERIODIC" },
        ]}
      />
      {type === "FIXED" ? (
        <Stack direction="row" gap={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DateInput
              name={path("startTime")}
              label="Starts at"
              placeholder=""
              minDate={minDate}
              maxDate={maxDate}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DateInput
              name={path("endTime")}
              label="Ends at"
              placeholder=""
              defaultTime="23:59"
              minDate={minDate}
              maxDate={maxDate}
            />
          </Box>
        </Stack>
      ) : (
        <>
          <ToggleButtonSelectInput name={path("daysOfWeek")} label="Days" options={WEEKDAYS} />
          <Stack direction="row" gap={1.5}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TimeInput name={path("localStartTime")} label="From" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TimeInput name={path("localEndTime")} label="Until" />
            </Box>
          </Stack>
          <Checkbox
            label="Only between certain dates"
            checked={showDates}
            onChange={(event) => {
              setDatesChosen(event.target.checked);
              if (!event.target.checked) {
                setValue(`boosts.${index}.startTime`, null, { shouldDirty: true });
                setValue(`boosts.${index}.endTime`, null, { shouldDirty: true });
              }
            }}
          />
          {showDates ? (
            <Stack direction="row" gap={1.5}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DateInput
                  name={path("startTime")}
                  label="Active from"
                  placeholder="Start of quest"
                  minDate={minDate}
                  maxDate={maxDate}
                  hideOpenButton
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DateInput
                  name={path("endTime")}
                  label="Active until"
                  placeholder="End of quest"
                  defaultTime="23:59"
                  minDate={minDate}
                  maxDate={maxDate}
                  hideOpenButton
                />
              </Box>
            </Stack>
          ) : null}
        </>
      )}
    </>
  );
};
